/**
 * Application detail + review actions (v3-14) — route /admin/applications/:id.
 *
 * Operators drive the intake lifecycle here: Start review / Approve & convert /
 * Decline. Approve converts the application into a Partner + draft Campaign and
 * links straight through to the campaign workflow.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AdminShell } from '../components/AdminShell';
import { ApiError } from '../lib/api';
import {
  approveApplication,
  declineApplication,
  getApplication,
  startApplicationReview,
  type ApplicationDetail,
  type ApplicationTransitionResult,
} from '../lib/adminApi';

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-4 border-b border-line py-2 text-sm last:border-0">
      <dt className="text-ink-500">{label}</dt>
      <dd className="col-span-2 text-ink">{value || '—'}</dd>
    </div>
  );
}

type Action = 'start-review' | 'approve' | 'decline';

export function AdminApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [app, setApp] = useState<ApplicationDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Action | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [creds, setCreds] = useState<ApplicationTransitionResult | null>(null);

  useEffect(() => {
    if (!id) return;
    let live = true;
    getApplication(id)
      .then((d) => live && setApp(d))
      .catch((err) => {
        if (!live || (err instanceof ApiError && err.status === 401)) return;
        setError(err instanceof ApiError ? err.message : 'Could not load this application.');
      });
    return () => {
      live = false;
    };
  }, [id]);

  async function run(action: Action) {
    if (!id || busy) return;
    if (action === 'decline' && !window.confirm('Decline this application? This cannot be undone.')) {
      return;
    }
    setBusy(action);
    setActionError(null);
    try {
      if (action === 'start-review') {
        await startApplicationReview(id);
      } else if (action === 'decline') {
        await declineApplication(id);
      } else {
        const res = await approveApplication(id);
        // Surface the auto-provisioned partner login (shown once). If no new
        // password (login already existed), go straight to the campaign.
        if (res.partner_temp_password) {
          setCreds(res);
          const fresh = await getApplication(id);
          setApp(fresh);
          return;
        }
        if (res.converted_campaign_id) {
          navigate(`/admin/campaigns/${res.converted_campaign_id}`);
          return;
        }
      }
      const fresh = await getApplication(id);
      setApp(fresh);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setActionError(err instanceof ApiError ? err.message : 'Action failed. Please try again.');
    } finally {
      setBusy(null);
    }
  }

  const status = app?.status;
  const canReview = status === 'submitted';
  const canApprove = status === 'submitted' || status === 'under_review';
  const canDecline = status === 'submitted' || status === 'under_review';

  return (
    <AdminShell title="Application">
      <Link to="/admin" className="mb-4 inline-block text-sm text-primary hover:underline">
        ← Back to queue
      </Link>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!error && !app && <p className="text-sm text-ink-500">Loading…</p>}
      {app && (
        <div className="rounded-2xl border border-line bg-white p-6 shadow-card">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-base font-semibold text-ink">{app.company_name}</h2>
            <span className="rounded-full bg-surface-muted px-2 py-0.5 text-xs font-medium text-ink-700">
              {app.status}
            </span>
          </div>
          <dl>
            <Row label="Reference" value={app.reference} />
            <Row label="Industry" value={app.industry} />
            <Row label="Website" value={app.website} />
            <Row label="Contact" value={`${app.contact_name} · ${app.email} · ${app.phone}`} />
            <Row label="Prize" value={`${app.prize_product} (${app.prize_value})`} />
            <Row label="Sponsorship" value={app.sponsorship_type} />
            <Row label="Objective" value={app.campaign_objective} />
            <Row label="Audience" value={app.target_audience} />
            <Row label="Preferred dates" value={app.preferred_dates} />
            <Row label="QR placement" value={app.qr_placement} />
            <Row label="Wants leads" value={app.wants_leads ? 'Yes' : 'No'} />
            <Row label="Wants AI marketing" value={app.wants_ai_marketing ? 'Yes' : 'No'} />
            <Row label="Notes" value={app.notes} />
            <Row
              label="Consent"
              value={`${app.consent_acknowledged ? 'Acknowledged' : 'No'} · ${app.consent_at}`}
            />
          </dl>

          {/* Review actions */}
          <div className="mt-6 border-t border-line pt-5">
            {actionError && <p className="mb-3 text-sm text-red-600">{actionError}</p>}

            {creds?.partner_temp_password && (
              <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm">
                <p className="font-semibold text-amber-900">
                  Partner login created — shown once. Hand these to the partner securely.
                </p>
                <dl className="mt-2 space-y-1">
                  <div className="flex gap-2">
                    <dt className="w-20 text-amber-800">Login URL</dt>
                    <dd className="font-mono text-ink">{window.location.origin}/partner/login</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-20 text-amber-800">Email</dt>
                    <dd className="font-mono text-ink">{creds.partner_login_email}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-20 text-amber-800">Password</dt>
                    <dd className="font-mono text-ink">{creds.partner_temp_password}</dd>
                  </div>
                </dl>
                <button
                  type="button"
                  onClick={() =>
                    navigator.clipboard?.writeText(
                      `Login: ${window.location.origin}/partner/login\nEmail: ${creds.partner_login_email}\nPassword: ${creds.partner_temp_password}`,
                    )
                  }
                  className="mt-2 rounded-md border border-amber-300 px-2 py-0.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                >
                  Copy credentials
                </button>
              </div>
            )}

            {app.status === 'approved' && app.converted_campaign_id && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm">
                <p className="font-semibold text-emerald-800">
                  Approved — converted to a partner + campaign.
                </p>
                <Link
                  to={`/admin/campaigns/${app.converted_campaign_id}`}
                  className="mt-1 inline-block font-semibold text-primary hover:underline"
                >
                  Open the campaign workflow →
                </Link>
              </div>
            )}

            {app.status === 'declined' && (
              <p className="text-sm text-ink-500">This application was declined.</p>
            )}

            {(canReview || canApprove || canDecline) && (
              <div className="flex flex-wrap gap-3">
                {canReview && (
                  <button
                    type="button"
                    onClick={() => run('start-review')}
                    disabled={!!busy}
                    className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-surface-muted disabled:opacity-50"
                  >
                    {busy === 'start-review' ? 'Starting…' : 'Start review'}
                  </button>
                )}
                {canApprove && (
                  <button
                    type="button"
                    onClick={() => run('approve')}
                    disabled={!!busy}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                  >
                    {busy === 'approve' ? 'Converting…' : 'Approve & convert'}
                  </button>
                )}
                {canDecline && (
                  <button
                    type="button"
                    onClick={() => run('decline')}
                    disabled={!!busy}
                    className="rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    {busy === 'decline' ? 'Declining…' : 'Decline'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </AdminShell>
  );
}

export default AdminApplicationDetailPage;
